// @view/workspace/AccountManagement.tsx

import React, { useCallback, useMemo, useState } from "react";
import type { AccountResponse } from "@ps-bridge/models/core/dataContracts";
import * as Option from "@utils/Option";
import { useOnInit, useIoEffectOption } from "@hooks/effects/useIoEffect";
import { ioOptionNone, ioOptionSome } from "@hooks/effects/useIoEffectWithCleanup";
import { useIoOptionState } from "@utils/useIoOptionState";
import { ok } from "@ps-bridge/ok";
import { ioEffect } from "@utils/effects/branded";
import * as IO from "@ps-bridge/interop/io";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import type { AccountManagementViewProps, AccountMembership } from "@ps-bridge/contracts/accountManagement";
import AccountManagementPanel, {
    type AccountManagementModalPhase,
    type AccountManagementViewPhase,
} from "@view/panels/workspace/AccountManagement.panel";

const AccountManagementComponent: React.FC<AccountManagementViewProps> = ({
    accountDetails,
    membership,
    passwordStep,
    passwordResetCompleted,
    passwordSubmitting,
    onPasswordStepChange,
    onPasswordResetCompletedChange,
    onRequestPasswordReset,
    onCompletePasswordReset,
    onInit,
}) => {
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [resetToken, setResetToken] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const passwordErrorState = useIoOptionState<string>(Option.none());
    const { setMaybe: setPasswordErrorMaybe, setSome: setPasswordErrorSome } = passwordErrorState;

    useOnInit(onInit);

    const closePasswordModalIo = useMemo(
        () =>
            IO.sequenceOk([
                ioEffect(() => {
                    setIsPasswordModalOpen(false);
                    setResetToken("");
                    setNewPassword("");
                    setConfirmPassword("");
                    return ok;
                }),
                setPasswordErrorMaybe(Option.none()),
                onPasswordStepChange("request"),
                onPasswordResetCompletedChange(false),
            ]),
        [
            onPasswordResetCompletedChange,
            onPasswordStepChange,
            setPasswordErrorMaybe,
            setConfirmPassword,
            setIsPasswordModalOpen,
            setNewPassword,
            setResetToken,
        ],
    );
    const closeOnCompleteIo = useMemo<OptionTs<IOts<Ok>>>(
        () =>
            passwordResetCompleted
                ? ioOptionSome(closePasswordModalIo)
                : ioOptionNone(),
        [closePasswordModalIo, passwordResetCompleted],
    );

    useIoEffectOption(closeOnCompleteIo, [closeOnCompleteIo]);

    const accountInfo = useMemo(
        () =>
            Option.map((details: AccountResponse) => {
                const firstName = Option.fold(Option.map(String)(details.firstName), () => "", (value) => value);
                const lastName = Option.fold(Option.map(String)(details.lastName), () => "", (value) => value);
                const fullName = [firstName, lastName].filter((value) => value.length > 0).join(" ");
                return {
                    email: String(details.email),
                    subscriptionStatus: String(details.status),
                    fullName: fullName || "Account",
                    organizationName: Option.fold(Option.map(String)(details.organizationName),  () => "Organization", (value) => value),
                };
            })(accountDetails),
        [accountDetails],
    );

    const membershipInfo = useMemo(
        () =>
            Option.map((value: AccountMembership) => ({
                role: String(value.role),
                status: String(value.status),
            }))(membership),
        [membership],
    );

    const handleSendReset = (): IOts<Ok> =>
        IO.sequenceOk([setPasswordErrorMaybe(Option.none()), onRequestPasswordReset(ok)]);

    const handleCompleteReset = (): IOts<Ok> => {
        if (!resetToken.trim()) {
            return setPasswordErrorSome("Enter the verification code from your email.");
        }
        if (!newPassword || !confirmPassword) {
            return setPasswordErrorSome("Enter and confirm your new password.");
        }
        if (newPassword.length < 8) {
            return setPasswordErrorSome("Password should be at least 8 characters.");
        }
        if (newPassword !== confirmPassword) {
            return setPasswordErrorSome("Passwords do not match.");
        }
        return IO.sequenceOk([
            setPasswordErrorMaybe(Option.none()),
            onCompletePasswordReset({
                token: resetToken.trim(),
                password: newPassword,
                confirmPassword,
            }),
        ]);
    };

    const viewPhase: AccountManagementViewPhase = Option.fold(
        accountDetails,
        () => "loading",
        () => "ready",
    );
    const modalPhase: AccountManagementModalPhase = !isPasswordModalOpen
        ? "closed"
        : passwordStep === "request"
            ? "request"
            : "complete";

    const fullNameText = Option.fold(accountInfo, () => "Account", (value) => value.fullName);
    const emailText = Option.fold(accountInfo, () => "-", (value) => value.email);
    const subscriptionStatusText = Option.fold(accountInfo, () => "-", (value) => value.subscriptionStatus);
    const organizationNameText = Option.fold(accountInfo, () => "Organization", (value) => value.organizationName);
    const membershipRoleText = Option.fold(membershipInfo, () => "Member", (value) => value.role);
    const membershipStatusText = Option.fold(membershipInfo, () => "Active", (value) => value.status);
    const modalEmailText = Option.fold(accountInfo, () => "-", (value) => value.email);
    const passwordErrorMessage = Option.fold(passwordErrorState.read, () => "", (value) => value);

    const openPasswordModalIo: IOts<Ok> = ioEffect(() => {
        setIsPasswordModalOpen(true);
        return ok;
    });
    const onResetTokenChange = useCallback((value: string): Ok => {
        setResetToken(value);
        return ok;
    }, []);
    const onNewPasswordChange = useCallback((value: string): Ok => {
        setNewPassword(value);
        return ok;
    }, []);
    const onConfirmPasswordChange = useCallback((value: string): Ok => {
        setConfirmPassword(value);
        return ok;
    }, []);
    const sendResetIo: IOts<Ok> = handleSendReset();
    const completeResetIo: IOts<Ok> = handleCompleteReset();

    return (
        <AccountManagementPanel
            viewPhase={viewPhase}
            modalPhase={modalPhase}
            fullNameText={fullNameText}
            emailText={emailText}
            subscriptionStatusText={subscriptionStatusText}
            organizationNameText={organizationNameText}
            membershipRoleText={membershipRoleText}
            membershipStatusText={membershipStatusText}
            modalEmailText={modalEmailText}
            passwordSubmitting={passwordSubmitting}
            passwordErrorMessage={passwordErrorMessage}
            resetToken={resetToken}
            newPassword={newPassword}
            confirmPassword={confirmPassword}
            onOpenPasswordModal={openPasswordModalIo}
            onClosePasswordModal={closePasswordModalIo}
            onSendReset={sendResetIo}
            onCompleteReset={completeResetIo}
            runIO={IO.run}
            onResetTokenChange={onResetTokenChange}
            onNewPasswordChange={onNewPasswordChange}
            onConfirmPasswordChange={onConfirmPasswordChange}
        />
    );
};

export default AccountManagementComponent;
